<template lang="pug">

mixin assessment-nav
  .assessment-nav.relative.mb-8(
    :class="[{ 'hidden': activeSlide === 'assessment-introduction' }]"
    :style="`width: ${navContainerWidth}; height: ${progressBarIconSize}`"
    v-if="!wordDoc && !audioDoc && activeSlide !== includedQuestionCount && includedQuestionCount > 1"
  )
    .progress-bar.absolute(
      :style="`height: ${progressBarThickness}; width: ${progressBarContainerWidth}`"
    )
      .base.absolute.rounded-full.w-full.h-full(
        :class="progressBarProps.bar.base"
      )
      .fill.absolute.rounded-full(
        :class="progressBarProps.bar.fill"
        :style="{ width: progressBarWidth }"
      )

    .pagination.absolute.grid(
      :style="`grid-template-columns: repeat(${includedQuestions.length}, 1fr)`"
      style="place-items: center"
      v-if="showPaginationIcons"
    )
      .dot-container.flex.items-center.justify-center(
        :style="`width: ${progressBarIconSize}; height: ${progressBarIconSize}`"
        :class="{ '-active': activeSlide === i }"
        :id="'assessment-dot-' + i"
        :key="q.id + attemptCount"
        v-for="(q, i) in includedQuestions"
      )
        span.dot-base.inline-flex.items-center.justify-center.w-full.h-full(
          :class="[{ 'correct': isCorrect(q), 'incorrect': !isCorrect(q) && isSubmitted(q), 'unanswered': i > activeSlide && !isSubmitted(q), 'active': i === activeSlide && !isSubmitted(q) }]"
        )
          .answered.rounded-full.inline-flex.items-center.justify-center.w-full.h-full(
            :class="[{ 'correct': isCorrect(q), 'incorrect': !isCorrect(q) && isSubmitted(q), 'active': i === activeSlide }, isCorrect(q) ? progressBarProps.icon.correct : progressBarProps.icon.incorrect]"
            v-if="isSubmitted(q)"
          )
            uc-icon(
              :style="`width: ${parseInt(progressBarIconSize) - 6}px; height: ${parseInt(progressBarIconSize) - 6}px`"
              :asImage="true"
              :icon="isCorrect(q) ? progressBarProps.icon.correctIcon : progressBarProps.icon.incorrectIcon"
            )

          .unanswered.w-full.h-full.relative(
            v-else=""
          )
            .active.transition-opacity.ease-out.delay-500(
              :class="i === activeSlide ? 'opacity-100' : 'opacity-0'"
            )
              .opacity-50.align-center.rounded-full(
                :class="progressBarProps.icon.activeOuter"
                :style="`width: ${parseInt(progressBarIconSize) + 8}px; height: ${parseInt(progressBarIconSize) + 8}px`"
              )
              .opacity-50.align-center.rounded-full(
                :class="progressBarProps.icon.activeOuter"
                :style="`width: ${parseInt(progressBarIconSize)}px; height: ${parseInt(progressBarIconSize)}px`"
              )
              .align-center.rounded-full(
                :class="progressBarProps.icon.activeCore"
                :style="`width: ${parseInt(progressBarIconSize) - 8}px; height: ${parseInt(progressBarIconSize) - 8}px`"
              )
            .inactive.transition-opacity.ease-out.delay-500(
              :class="i === activeSlide ? 'opacity-0' : 'opacity-100'"
            )
              .opacity-50.align-center.rounded-full(
                :class="progressBarProps.icon.unansweredOuter"
                :style="`width: ${parseInt(progressBarIconSize) - 4}px; height: ${parseInt(progressBarIconSize) - 4}px`"
              )
              .align-center.rounded-full(
                :class="progressBarProps.icon.unansweredCore"
                :style="`width: ${parseInt(progressBarIconSize) - 8}px; height: ${parseInt(progressBarIconSize) - 8}px`"
              )

.assessment.flex.flex-col.gap-6.items-center.justify-center.max-w-screen-xl.px-6.my-8.mx-auto(
  :class="{ 'reduce-motion': suspend.reducedMotion }"
)
  uc-preload-sfx(name="continue")

  //- progress bar and pagination dots
  +assessment-nav

  //- assessment headings (hide for results)
  .text-center.flow-root(v-if="!wordDoc && !audioDoc")
    .slides.question-headings(
      v-if="activeSlide === 'assessment-introduction' || activeSlide < includedQuestionCount"
    )
      .slide(
        id="assessment-introduction"
        :class="{ 'slide--active': activeSlide === 'assessment-introduction' }"
        :variant-slide="getSlideVariant()"
      )
        uc-editable.heading.text-3xl.text-primary-700.dark_text-primary-300(
          :edit-path="`${editPath}.title`"
          :k="`${id}.title`"
          :text="title"
        )
      .slide(
        :class="{ 'slide--active': activeSlide === i }"
        :data-index="i"
        :key="q.id + attemptCount"
        :variant-slide="getSlideVariant()"
        v-for="(q, i) in includedQuestions"
      )
        uc-text.heading.text-primary-700.dark_text-primary-300(
          :values="{ n: i + 1 }"
          text="Question ${n}"
          v-if="showPaginationIcons"
        )
        uc-text.heading.text-primary-700.dark_text-primary-300(
          :values="{ n: i + 1, t: includedQuestions.length }"
          text="Question ${n}/${t}"
          v-else
        )
      .slide(
        id="assessment-result"
        :class="{ 'slide--active': activeSlide === includedQuestionCount }"
        :variant-slide="getSlideVariant()"
      )
        uc-text.heading.text-primary-700.dark_text-primary-300(
          :k="`${id}.result`"
          text="Result"
        )

  //- assessment intro, question and results slides
  .slides.questions.flow-root.screen--no-fade
    //- INTRO SLIDE
    .slide(
      id="assessment-introduction"
      :class="{ 'slide--active': activeSlide === 'assessment-introduction' }"
      :variant-slide="getSlideVariant()"
    )
      .text-center
        audio-marker(:name="`${id}-introduction`")

        uc-screen(
          :edit-path="`${editPath}.assessment.introduction.templates.${i}`"
          :nested="true"
          :screen="{ ...s, 'placeholder-values': placeholderValues }"
          v-for="(s, i) in introduction.templates"
          v-if="introduction.templates && !passed && !failed"
        )

        .flex.flex-col.items-center.justify-center.gap-10.m-auto.prose.prose-stack(v-else="")
          uc-editable.text-center(
            :edit-path="`${editPath}.assessment.introduction`"
            :k="`${id}.assessment.introduction`"
            :text="introductionText"
            :values="{ total, required, totalEnglish, requiredEnglish }"
            v-if="!passed && !failed"
          )

          .flex.flex-col.items-center.justify-center.gap-4.text-primary-900.dark_text-white.text-center.w-full
            .flex.flex-col.sm_flex-row.items-stretch.gap-4.justify-center.w-full(
              v-if="!wordDoc && !audioDoc"
            )
              .intro-text-panel.flex-1.rounded.p-10.m-0.w-full.text-2xl.flex.flex-col.items-center.justify-center(
                :class="[textPanelClasses]"
                v-if="!passed && !failed"
              )
                uc-text(
                  text="Questions:{fbr} **${total}**"
                  :values="{ total }"
                )

              .intro-text-panel.flex-1.rounded.p-10.m-0.w-full.text-2xl.flex.flex-col.items-center.justify-center(
                :class="textPanelClasses"
                v-if="passmark && passmark > 0"
              )
                uc-text(
                  text="Pass mark:{fbr} **${passmark}%**"
                  :values="{ passmark }"
                )

              .intro-text-panel.flex-1.rounded.p-10.m-0.w-full.text-2xl.flex.flex-col.items-center.justify-center(
                :class="textPanelClasses"
                v-if="passed || failed"
              )
                uc-text(
                  text="Score:{fbr} **${score}%**"
                  :values="{ score }"
                )

              .intro-text-panel.flex-1.rounded.p-10.m-0.w-full.text-2xl.flex.flex-col.items-center.justify-center(
                :class="textPanelClasses"
                v-else-if="maxAttempts && !passed && !failed"
              )
                uc-text(
                  text="Attempts:{fbr} **${attemptsRemaining}**"
                  :values="{ attemptsRemaining }"
                  v-if="attemptCount === 0"
                )
                uc-text(
                  text="Attempts remaining:{fbr} **${attemptsRemaining}**"
                  :values="{ attemptsRemaining }"
                  v-else=""
                )

            .intro-text-panel.rounded.p-10.w-full(
              :class="textPanelClasses"
              v-if="passed || failed"
            )
              //- expose these for translations
              //- text="Congratulations, you have passed the assessment!"
              //- text="Unfortunately, you have failed the assessment and no further attempts are available."
              uc-text.text-center.p-0.m-0(
                :k="`${id}.assessment.intro.passed`"
                :text="assessment?.intro?.passed || `Congratulations, you have passed the assessment!`"
                type="p"
                v-if="passed"
              )
              uc-text.text-center.p-0.m-0(
                :k="`${id}.assessment.intro.failed`"
                :text="assessment?.intro?.failed || `Unfortunately, you have failed the assessment and no further attempts are available.`"
                type="p"
                v-else-if="failed"
              )
            template(
              v-else
            )
              .intro-text-panel.rounded.p-10.w-full(
                :class="textPanelClasses"
                v-if="instructionText && !audioDoc && passmark && passmark > 0"
              )
                uc-text.text-center(
                  :k="`${id}.assessment.instructions`"
                  :text="instructionText"
                  :values="{ total, required, totalEnglish, requiredEnglish }"
                )

              .intro-text-panel.border-dashed.border-2.border-primary-200.rounded.p-10.w-full(
                :class="textPanelClasses"
                v-if="isEditModeAvailable && !audioDoc && !wordDoc && (count < includedQuestionCount || includedQuestionCount < questions.length)"
              )
                uc-text.p-0.m-0.font-bold(
                  text="Please note:"
                  type="p"
                )
                uc-text.p-0.m-0(
                  text="This test section has a bank of questions. To allow for previewing and editing, all **${includedQuestionCount}** questions from the bank are being shown. Your users will only be shown **${count}** questions in the published course."
                  :values="{ includedQuestionCount, count }"
                  type="p"
                  v-if="count < includedQuestionCount"
                )
                uc-text.p-0.m-0(
                  text="**${excludedQuestionCount}** questions are removed based on related course content you have excluded."
                  type="p"
                  :values="{ excludedQuestionCount }"

                  v-if="includedQuestionCount < questions.length"
                )


        template(
          v-if="passed"
        )
          uc-button.btn.btn-primary.ripple.w-full.sm_w-auto.mt-10(
            @click="scrollDown"
            data-sfx="continue"
            text="Continue"
          )

        template(
          v-else-if="failed"
        )
          uc-button.btn.btn-primary.ripple.w-full.sm_w-auto.mt-10(
            @click="exitCourse"
            data-sfx="close"
            text="Close"
            v-if="!hasSessionEnded"
            :disabled="hasSessionEnded"
          )
          .close-message-container.w-full.flex.justify-center.items-center.mt-10(
            v-if="!wordDoc && !audioDoc && hasSessionEnded"
          )
            .screen-exit-message.text-center.border-radius.border-b-secondary-500.transition.opacity-90.bg-paper.p-6
              uc-text.text-xl.editable.editable--prose.prose(
                text="If the window does not automatically close, please close it manually."
              )

        template(
          v-else
        )
          uc-button.btn.btn-primary.ripple.w-full.sm_w-auto.mt-10(
            @click="start"
            data-sfx="continue"
            :disabled="audioLocked"
          )
            uc-text(text="Start")

          div(v-if="wordDoc && !audioDoc && count")
            uc-text.italic(
              :values="{ count, includedQuestionCount }"
              text="[ **Note:** The **${count}** questions presented to the user are selected randomly from the following set of **${includedQuestionCount}** questions. ]"
              type="p"
            )

    //- QUESTION SLIDES
    .slide(
      :class="{ 'slide--active': activeSlide === i }"
      :data-index="i"
      :id="'assessment-' + i"
      :key="q.id + attemptCount"
      :variant-slide="getSlideVariant()"
      v-for="(q, i) in includedQuestions"
    )
      audio-marker(:name="`${q.id}`")

      transition-expand
        template(v-if="activeSlide === i")

      .slide__content
        p(v-if="wordDoc && !audioDoc")
          uc-text.font-bold(
            :values="{ n: i + 1 }"
            text="Question ${n}"
            type="h3"
          )

        uc-screen(
          :editPath="`${editPath}.assessment.questions.${i}`"
          :nested="true"
          :screen="screenForQuestion(q)"
        )
          transition-fade(:delay="1")
            .text-right.w-full.my-8(v-if="suspend.completed[q.id]")
              uc-button.btn.btn-primary.ripple.w-full.sm_w-auto(
                @click="advanceSlide"
                data-sfx="continue"
                :disabled="audioLocked"
              )
                uc-text(text="Continue")

    //- RESULT SLIDE
    .slide(
      id="assessment-result"
      :class="{ 'slide--active': activeSlide === includedQuestionCount }"
      :variant-slide="getSlideVariant()"
      v-if="!wordDoc || audioDoc"
    )
      .assessment-result-scoremeter(
        :class="{ 'dark': dark }"
        :style="{ '--score-percent': animatedScore, '--passmark': passmark, '--main-size': mediumOrLarger ? '300px' : smallOrLarger ? '260px' : '220px', '--score-text-size': mediumOrLarger ? '80px' : smallOrLarger ? '70px' : '60px' }"
        :aria-label="`Score meter showing ${score}%. Pass mark is ${passmark}%.`"
        role="img"
        v-if="!audioDoc"
      )
        .score-ticks.aria-hidden
          //- create line ticks from and including the passmark round to 100%
          .tick(
            :style="{ '--tick-index': passmark - 1 + n }"
            :id="`tick-${passmark - 1 + n}`"
            v-for="n in 100 - passmark + 1"
          )
        .score-dial.aria-hidden
        .score-text.prose.aria-hidden
          uc-text.score.text-center(
            class="w-[4ch]"
            :text="`${Math.round(animatedScore)}%`"
            type="span"
          )

      .prose-stack.prose.text-center.mx-auto
        div(v-if="audioDoc || passed")
          audio-marker(:name="`${id}-results-passed`")

          uc-text(
            style="margin-top: 60px; margin-bottom: 10px;"
            text="Well done."
            type="h3"
          )

          template(
            v-if="!audioDoc && resultsPassed.includeScore && resultsScoreText"
          )
            uc-text(
              :k="`${id}.assessment.results.score` || resultsScoreText"
              :text="resultsScoreText"
              :values="{ total, correct }"
              type="p"
            )

          uc-editable(
            :edit-path="`${editPath}.assessment.results.passed`"
            :k="`${id}.assessment.results.passed` || resultsPassed.text"
            :text="resultsPassed.text"
            :values="{ total, correct }"
          )

          .-center(style="margin-top: 60px;")
            uc-button.btn.btn-primary.unlock.ripple.w-full.sm_w-auto(
              @click="scrollDown"
              data-sfx="continue"
              text="Continue"
            )

        div(v-if="audioDoc || !passed")
          template(v-if="audioDoc || !hasExceededMaxAttempts")
            audio-marker(:name="`${id}-results-failed`")

            uc-text(
              style="margin-top: 60px; margin-bottom: 10px;"
              text="Not quite."
              type="h3"
            )

            p(
              v-if="!audioDoc && ((resultsScoreText && resultsScoreText !== resultsFailedText) || resultsRequiredText)"
            )
              uc-text.block(
                :k="`${id}.assessment.results.score` || resultsScoreText"
                :text="resultsScoreText"
                :values="{ correct, total }"
                type="span"
                v-if="resultsScoreText !== resultsFailedText"
              )
              uc-text.block(
                :k="`${id}.assessment.results.required` || resultsRequiredText"
                :text="resultsRequiredText"
                :values="{ required }"
                type="span"
                v-if="resultsRequiredText"
              )
              uc-text.block(
                :k="`${id}.assessment.results.attempt` || resultsAttemptText"
                :text="resultsAttemptText"
                :values="{ attemptsRemaining }"
                type="span"
                v-if="resultsAttemptText"
              )

            uc-editable(
              :edit-path="`${editPath}.assessment.results.failed`"
              :k="`${id}.assessment.results.failed` || resultsFailedText"
              :text="resultsFailedText"
              :values="{ total, correct }"
            )

          template(v-if="audioDoc || hasExceededMaxAttempts")
            audio-marker(:name="`${id}-results-max-attempts`")

            uc-text(
              style="margin-top: 60px; margin-bottom: 10px;"
              text="Failed."
              type="h3"
            )

            uc-editable(
              :edit-path="`${editPath}.assessment.results.max-attempts`"
              :k="`${id}.assessment.results.max-attempts` || resultsMaxAttemptsText"
              :text="resultsMaxAttemptsText"
              :values="{ maxAttempts, attemptCount }"
            )

          .-center(style="margin-top: 60px;")
            uc-button.btn.btn-primary.ripple.w-full.sm_w-auto(
              @click="exitCourse"
              data-sfx="close"
              text="Close"
              v-if="hasExceededMaxAttempts && !hasSessionEnded"
              :disabled="hasSessionEnded"
            )
            uc-button.btn.btn-primary.ripple.w-full.sm_w-auto(
              @click="tryAgain"
              data-sfx="continue"
              text="Try again"
              v-else-if="!hasExceededMaxAttempts && !hasSessionEnded"
            )

            .close-message-container.w-full.flex.justify-center.items-center(v-if="!wordDoc && !audioDoc && hasSessionEnded")
              .screen-exit-message.text-center.border-radius.border-b-secondary-500.transition.opacity-90.bg-paper.p-6
                uc-text.text-xl.editable.editable--prose.prose(
                  text="If the window does not automatically close, please close it manually."
                )
</template>

<script lang="ts">
import { computed, nextTick, reactive, ref } from 'vue';
import {
  getPassmark,
  set,
  get,
  passed,
  failed,
  setCompleted,
  setExit,
  getScore,
  setScore,
  setSessionTime
} from '../lib/SCORM';
import { shuffle } from 'gsap/gsap-core';
import { play, stop } from '../lib/audio';
import useScreenAudio, {
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';
import { schemaForTemplate } from '../lib/screen-schemas';
import useAudio from '../stores/useAudio';
import { mapActions } from 'pinia';
import useQuizzes from '../stores/useQuizzes';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import useTranslations from '../stores/useTranslations';
import evaluateCondition from '../lib/evaluateCondition';
import isEditModeAvailable, { isAuthoring } from '../lib/isEditModeAvailable';
import useContent from '../stores/useContent';
import findAncestorIds from '../lib/findAncestorIds';
import { computeConditionIds } from '../lib/evaluateCondition';
import useTheme from '../stores/useTheme';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { twMerge } from '../lib/mergeStyles';

const scormPassmark = getPassmark();

function applyDefaultTemplate(q: UcScreen) {
  const template = q.template || 'question';

  return { ...q, template };
}

export default {
  inject: ['suspend', 'audioDoc', 'wordDoc', 'isEditModeAvailable'],
  provide: { sectionId: '___' },
  inheritAttrs: false,
  emits: ['exit'],
  props: {
    assessment: { type: Object, required: true },
    title: { type: String, default: '' },
    id: { type: String, required: true },
    editPath: { default: '', type: String },
    dark: { type: Boolean, default: false }
  },
  setup(props, { emit }) {
    const { getAudioURL } = useAudio();
    const { translations } = useTranslations();
    const { compiled } = useContent();
    const {
      completeCourse: completeCourseAction,
      failCourse: failCourseAction
    } = useQuizzes();
    const theme = useTheme();
    const breakpoints = useBreakpoints(breakpointsTailwind);
    const extraSmallDevice = breakpoints.smaller('sm');
    const smallOrLarger = breakpoints.greaterOrEqual('sm');
    const mediumOrLarger = breakpoints.greaterOrEqual('md');
    const largeOrLarger = breakpoints.greaterOrEqual('lg');

    // Schema-driven audio. The assessment props don't include a top-level `screen`
    // object, so we synthesise one from the component's own props. Nesting
    // `assessment` preserves the translation-key convention used in the template
    // (`:k="\`${id}.assessment.introduction\`"` etc.).
    const assessmentSchema = schemaForTemplate('assessment');
    type AssessmentIntroScreen = NarratableScreen & {
      title?: string;
    };
    /** Populated from Options `syncPlaceholderValues` before JiT resolves intro/results. */
    const placeholderValues = reactive<Record<string, string | number>>({
      total: 0,
      required: 0,
      totalEnglish: 0,
      requiredEnglish: 0
    });
    const syntheticScreen = computed<AssessmentIntroScreen>(() => ({
      id: props.id,
      title: props.title ?? undefined,
      template: 'assessment',
      assessment: props.assessment,
      'placeholder-values': placeholderValues
    }));
    const { resolveSlot } = useScreenAudio(syntheticScreen, assessmentSchema);
    const { resolveChildIntro, audioAiJitEnabled } = useChildScreenAudio();

    return {
      getAudioURL,
      translations,
      compiled,
      theme,
      extraSmallDevice,
      smallOrLarger,
      mediumOrLarger,
      largeOrLarger,
      emit,
      completeCourseAction,
      failCourseAction,
      resolveSlot,
      resolveChildIntro,
      audioAiJitEnabled,
      placeholderValues
    };
  },
  data() {
    const { questions, introduction, instructions, results, count } =
      this.assessment;

    const shuffleQuestions = this.assessment['shuffle-questions'] ?? true;
    const maxAttempts = this.assessment['max-attempts'] ?? null;
    const hasSessionEnded = ref(false);

    return {
      questions,
      introduction,
      instructions,
      results,
      count,
      shuffleQuestions,
      maxAttempts,
      activeSlide: 'assessment-introduction',
      attemptCount: 0,
      correctCount: 0,
      passed: false,
      failed: false,
      openFeedback: null,
      submitted: [],
      includedQuestions: 0,
      currentAudio: null,
      animatedScore: 0,
      progressBarSpacing: '10px',
      hasSessionEnded,
      twMerge
    };
  },
  computed: {
    passmark() {
      return this.assessment.passmark ?? scormPassmark;
    },
    introductionText() {
      // if we don't have a separate instructions prop we extract the paras with placeholder values from the introduction and use those
      if (
        this.suspend.locale === 'en-GB' &&
        !this?.instructions &&
        this.introduction.match(/\${\S+}/)
      ) {
        return this.introduction
          .split('\n\n')
          .filter(text => !text.match(/\${\S+}/))
          .join('\n\n');
      } else {
        return this.introduction;
      }
    },
    instructionText() {
      // if we have a separate instructions prop, use that
      // otherwise we extract the paras with placeholder values from the introduction text
      if (this?.instructions) {
        return this?.instructions;
      } else if (
        this.suspend.locale === 'en-GB' &&
        this.introduction.match(/\${\S+}/)
      ) {
        const filteredIntroText = this.introduction
          .split('\n\n')
          .filter(text => text.match(/\${\S+}/))
          .join('\n\n');
        return filteredIntroText;
      } else {
        return null;
      }
    },
    resultsPassed() {
      /**
       * return an object here with a boolean for including the score (new layout) and the text
       * use the new layout, with a separate correct/total box, if it's en-GB or we have the new translation
       */

      // override via the YAML or use the default text (both old and new)
      if (this?.results?.passed) {
        return {
          includeScore: this.resultsScoreText ? true : false,
          text: this?.results?.passed
        };
      } else {
        // expose these strings for the translate script
        // text="You have answered **${correct}** out of ${total} correctly and completed the test section."
        // text="You have achieved the pass mark and completed the test section."
        const textOrig =
          'You have answered **${correct}** out of ${total} correctly and completed the test section.';
        const textNew =
          'You have achieved the pass mark and completed the test section.';
        const hasTranslation = this.hasTranslation(textNew);
        const newLayout = this.suspend.locale === 'en-GB' || hasTranslation;
        return {
          includeScore: newLayout,
          text: newLayout ? textNew : textOrig
        };
      }
    },
    resultsFailedText() {
      // override via the YAML or use the default text (both old and new)
      if (this?.results?.failed) {
        return this?.results?.failed;
      } else {
        // expose these strings for the translate script
        // text="You answered **${correct} out of ${total}** correctly."
        // text="You didn't answer enough questions correctly to achieve the pass mark. Please try again."
        const textOrig =
          'You answered **${correct} out of ${total}** correctly.';
        const textNew =
          "You didn't answer enough questions correctly to achieve the pass mark. Please try again.";
        const hasTranslation = this.hasTranslation(textNew);
        return this.suspend.locale === 'en-GB' || hasTranslation
          ? textNew
          : textOrig;
      }
    },
    resultsScoreText() {
      // override via the YAML or use the default text (both old and new)
      if (this?.results?.score) {
        return this?.results?.score;
      } else {
        // expose these strings for the translate script
        // text="You answered **${correct} out of ${total}** correctly."
        // text="You answered **${correct}** out of **${total}** correctly."
        const textOrig =
          'You answered **${correct} out of ${total}** correctly.';
        const textNew =
          'You answered **${correct}** out of **${total}** correctly.';
        const hasTranslation = this.hasTranslation(textNew);
        return this.suspend.locale === 'en-GB' || hasTranslation
          ? textNew
          : textOrig || null;
      }
    },
    resultsRequiredText() {
      // override via the YAML or use the default text
      if (this?.results?.required) {
        return this?.results?.required;
      } else {
        // expose these strings for the translate script
        // text="You must answer at least ${required} correctly to pass the course."
        // text="You must answer at least **${required}** correctly to pass the course."
        const textOrig =
          'You must answer at least ${required} correctly to pass the course.';
        const textNew =
          'You must answer at least **${required}** correctly to pass the course.';
        const hasTranslation = this.hasTranslation(textNew);
        return this.suspend.locale === 'en-GB' || hasTranslation
          ? textNew
          : textOrig || null;
      }
    },
    resultsAttemptText() {
      // override via the YAML or use the default text (both old and new)
      if (this?.results?.['failed-attempt']) {
        return this?.results?.['failed-attempt'];
      } else {
        // expose these strings for the translate script
        // text="You have **${attemptsRemaining}** attempt remaining."
        // text="You have **${attemptsRemaining}** attempts remaining."
        const textNewAttempts =
          'You have **${attemptsRemaining}** attempt remaining.';
        const textNewAttemptsPlural =
          'You have **${attemptsRemaining}** attempts remaining.';

        if (this.maxAttempts) {
          return this.attemptsRemaining > 1
            ? this.hasTranslation(textNewAttemptsPlural) ||
                textNewAttemptsPlural
            : this.hasTranslation(textNewAttempts) || textNewAttempts;
        }
        return null;
      }
    },
    resultsMaxAttemptsText() {
      // override via the YAML or use the default text
      if (this?.results?.['failed-all-attempts']) {
        return this?.results?.['failed-all-attempts'];
      } else {
        // expose these strings for the translate script
        // text="Unfortunately, you didn't answer enough questions correctly to achieve the pass mark. As you have no more attempts remaining for this assessment, the course has been marked as failed."
        const text =
          "Unfortunately, you didn't answer enough questions correctly to achieve the pass mark. As you have no more attempts remaining for this assessment, the course has been marked as failed.";
        const hasTranslation = this.hasTranslation(text);
        return this.suspend.locale === 'en-GB' || hasTranslation
          ? text
          : text || null;
      }
    },
    correct() {
      return this.correctCount;
    },
    required() {
      return Math.ceil((this.passmark / 100) * this.totalCount);
    },
    score() {
      if (this.passed || this.failed) {
        return getScore() || 0;
      } else if (this.attemptCount === 0) {
        return 0;
      } else {
        return Math.round((this.correctCount / this.totalCount) * 100);
      }
    },
    excludedQuestionCount() {
      return this.questions.length - this.includedQuestions.length;
    },
    includedQuestionCount() {
      return this.includedQuestions.length || 0;
    },
    totalCount() {
      // The total is always the size of the question set actually in play.
      // `includedQuestions` is set imperatively at start/reset and already encodes the
      // edit-vs-published reduction (filterQuestions caps to `count` outside edit mode),
      // so deriving from it keeps score/total consistent even if edit/preview is toggled
      // mid-assessment. Branching on the reactive `isEditModeAvailable` here desynced the
      // score from the answered set. [VP hotfix]
      return this.includedQuestionCount;
    },
    total() {
      return this.totalCount;
    },
    // legacy placeholders
    correctEnglish() {
      return this.correct;
    },
    requiredEnglish() {
      return this.required;
    },
    totalEnglish() {
      return this.total;
    },
    filterQuestions() {
      let filtered = this.questions.map(applyDefaultTemplate);

      //- exclude questions with matching hidden related-content IDs (units/screens)
      if (this.compiled?.hidden) {
        filtered = filtered.filter(q => {
          if (q['related-content']) {
            const isHidden = evaluateCondition(
              q['related-content'],
              this.compiled.hidden
            );

            const allRelatedIDs = computeConditionIds(q['related-content']);
            const isAnyRelatedAncestorHidden = allRelatedIDs.some(id => {
              let ancestorIDs = findAncestorIds(this.compiled.pages, id) || [];
              // remove the related ID to only include the related content's parents
              if (ancestorIDs?.length > 1) ancestorIDs.pop();
              const isAncestorHidden = evaluateCondition(
                { any: 1, of: ancestorIDs },
                this.compiled.hidden
              );
              return isAncestorHidden;
            });
            return isHidden || isAnyRelatedAncestorHidden ? false : true;
          }
          return true;
        });
      }

      //- don't filter by tags, shuffle, or reduce Qs in docs or edit mode.
      //- `isAuthoring` is a stable per-session flag (true for the whole AT
      //- Lite / author preview); `isEditModeAvailable` tracks the live
      //- view/edit toggle, so it flips to false in preview and lets the
      //- assessment shuffle. Inline edits address questions positionally, so a
      //- shuffled render order writes edits onto the wrong question. Gate on
      //- isAuthoring so the preview never reorders regardless of toggle.
      if (
        !this.wordDoc &&
        !this.audioDoc &&
        !this.isEditModeAvailable &&
        !isAuthoring
      ) {
        //- filter out any tagged questions if those tags are not present e.g. from builders
        if (this.suspend.tags) {
          filtered = filtered.filter(q => {
            if (q.tag) {
              const isTagged = evaluateCondition(q.tag, this.suspend.tags);
              return isTagged;
            }
            return true;
          });
        }

        if (this.shuffleQuestions) {
          filtered = shuffle(filtered);
        }

        if (this.count) {
          let results = [];

          //- add compulsory questions first until we reach the count
          for (let i = 0; i < filtered.length; i++) {
            if (filtered[i].compulsory && results.length < this.count) {
              results.push(filtered[i]);
            }
          }

          //- add non-compulsory questions until we reach the count
          for (let i = 0; i < filtered.length; i++) {
            if (!filtered[i].compulsory && results.length < this.count) {
              results.push(filtered[i]);
            }
          }

          if (this.shuffleQuestions) {
            results = shuffle(results);
          }

          filtered = results;
        }
      }

      return filtered;
    },
    audioLocked() {
      const locked =
        this.compiled?.course?.audiolocked && this.suspend.audioplaying;
      return locked;
    },
    progressBarWidth() {
      if (typeof this.activeSlide !== 'number') return '0%';

      const progress =
        (this.activeSlide / (this.includedQuestionCount - 1)) * 100;
      //- limit between 0 and 100 for icons or 2 and 100 for no icons (shows a tiny bit of progress for the 1st question)
      return `${Math.min(
        Math.max(progress, this.showPaginationIcons ? 0 : 2),
        100
      )}%`;
    },
    navContainerWidth() {
      // Get device-specific constraints
      const deviceConstraints = this.getDeviceConstraints();
      const questionCount = this.includedQuestionCount;

      // Calculate optimal width based on question count and device
      return this.calculateOptimalWidth(questionCount, deviceConstraints);
    },
    progressBarContainerWidth() {
      // use the full width of the nav container if it's set in yaml, otherwise use the calculated width (minus 1 grid-column width as the icons are centered in their column layout and we don't want the extra 1/2 grid col on each end making the progress bar too wide)
      return this.assessment['progress-bar']?.width &&
        this.assessment['progress-bar']?.['hide-icons']
        ? '100%'
        : `${
            parseInt(this.navContainerWidth) -
            parseInt(this.navContainerWidth) / this.includedQuestions.length
          }px`;
    },
    progressBarThickness() {
      return this.assessment['progress-bar']?.['line-thickness'] ?? '4px';
    },
    showPaginationIcons() {
      const hideIcons =
        this.assessment['progress-bar']?.['hide-icons'] ?? false;
      if (hideIcons) return false;

      // hide pagination when question count exceeds device max otherwise use the yaml setting or default to true
      const deviceConstraints = this.getDeviceConstraints();
      const questionCount = this.includedQuestionCount;

      return questionCount > deviceConstraints.maxQuestions ? false : true;
    },
    progressBarIconSize() {
      // Get device-specific constraints
      const deviceConstraints = this.getDeviceConstraints();

      return `${deviceConstraints.iconSize}px`;
    },
    hasExceededMaxAttempts() {
      return this.maxAttempts !== null && this.attemptCount >= this.maxAttempts;
    },
    attemptsRemaining() {
      if (this.maxAttempts === null) return null;
      return Math.max(0, this.maxAttempts - this.attemptCount);
    },
    textPanelClasses() {
      return twMerge(
        'bg-primary-100/80 dark_bg-gray-900/80',
        this.assessment?.styles?.['text-panels']
      );
    },
    progressBarProps() {
      // return an object of merged classes for the progress bar icon states
      const customStyles = this.assessment?.['progress-bar']?.styles;
      return {
        bar: {
          base: twMerge(
            'bg-primary-300 dark_bg-primary-600',
            customStyles?.bar?.base
          ),
          fill: twMerge(
            'bg-primary-700 dark_bg-primary-200',
            customStyles?.bar?.fill
          )
        },
        icon: {
          correct: twMerge(
            'text-white bg-primary-500 dark_bg-primary-400',
            customStyles?.icon?.correct
          ),
          incorrect: twMerge(
            'text-white bg-primary-900 dark_bg-primary-600',
            customStyles?.icon?.incorrect
          ),
          activeCore: twMerge(
            'bg-gray-50',
            customStyles?.icon?.['active-core']
          ),
          activeOuter: twMerge(
            'bg-primary-300 dark_bg-primary-500',
            customStyles?.icon?.['active-outer']
          ),
          unansweredCore: twMerge(
            'bg-gray-50',
            customStyles?.icon?.['unanswered-core']
          ),
          unansweredOuter: twMerge(
            'bg-primary-300 dark_bg-primary-500',
            customStyles?.icon?.['unanswered-outer']
          ),
          correctIcon:
            this.assessment?.['progress-bar']?.['correct-icon'] ?? 'charm:tick',
          incorrectIcon:
            this.assessment?.['progress-bar']?.['incorrect-icon'] ??
            'charm:cross'
        }
      };
    }
  },
  async mounted() {
    const _this = this;

    // load the saved states from SCORM data
    this.attemptCount = get('assessment.attemptCount') || 0;
    this.includedQuestions =
      this.attemptCount === 0 ? [] : this.filterQuestions;
    this.correctCount =
      this.includedQuestions.length > 0
        ? this.getCorrectCount()
        : get('assessment.correctCount') || 0;
    this.passed = get('passed') || false;
    this.failed = get('failed') || false;

    await nextTick();

    this.$el.addEventListener('submit', e => {
      const i = +e.target.getAttribute('data-index');
      this.submitted.push(i);

      setTimeout(() => {
        this.openFeedback = e.target.id;
      }, 500);
    });

    if (this.attemptCount === 0) {
      this.resetQuestions();
    }

    this.syncPlaceholderValues();

    window.addEventListener('tagschanged', e => {
      //- this.resetQuestions();
    });

    const el = this?.$el;
    const page = el?.closest('.page');

    if (page) {
      ScrollTrigger.create({
        scroller: el.closest('.scroller'),
        trigger: el,
        start: 'top 45%',
        end: 'bottom 55%',

        async onToggle({ isActive }) {
          await nextTick();

          if (isActive && el.offsetHeight > 0) {
            if (
              _this.activeSlide === 'assessment-introduction' &&
              !_this.passed &&
              !_this.failed
            ) {
              if (_this.audioAiJitEnabled) {
                _this.syncPlaceholderValues();
                const audio = await _this.resolveSlot('intro');
                if (audio) play(audio);
              } else {
                const introAudio = _this.getAudioURL(
                  `${_this.id}-introduction`
                );
                if (introAudio) play(introAudio);
              }
            } else if (_this.currentAudio) {
              if (_this.audioAiJitEnabled) {
                _this.syncPlaceholderValues();
                // currentAudio is either '{id}-results-{key}' or a question id
                const resultPrefix = `${_this.id}-results-`;
                let audio: string | null = null;
                if (_this.currentAudio.startsWith(resultPrefix)) {
                  const slotKey = `results-${_this.currentAudio.slice(resultPrefix.length)}`;
                  const overrideText = _this.passed
                    ? _this.resultsPassed.text
                    : _this.hasExceededMaxAttempts
                      ? _this.resultsMaxAttemptsText
                      : _this.resultsFailedText;
                  audio = await _this.resolveSlot(slotKey, overrideText);
                } else {
                  // Re-entry on a question slide — find the question by stored id
                  const q = (_this.includedQuestions || []).find(
                    (q: NarratableScreen) => q.id === _this.currentAudio
                  );
                  if (q) {
                    audio = await _this.resolveChildIntro({
                      ...q,
                      'placeholder-values': _this.placeholderValues
                    });
                  }
                }
                if (audio) play(audio);
              } else {
                play(_this.getAudioURL(_this.currentAudio));
              }
            }
          }
        }
      });
    }
  },
  methods: {
    /** Aligns JiT `fillTemplates` with `uc-text` `:values` for intro / result overrides. */
    syncPlaceholderValues() {
      Object.assign(this.placeholderValues, {
        total: this.total,
        required: this.required,
        totalEnglish: this.totalEnglish,
        requiredEnglish: this.requiredEnglish,
        correct: this.correct,
        correctCount: this.correctCount,
        totalCount: this.totalCount,
        attemptsRemaining: this.attemptsRemaining,
        passmark: this.passmark,
        score: this.score
      });
    },
    screenForQuestion(q: NarratableScreen) {
      const base = this.assessment['force-instructions']
        ? {
            ...q,
            'force-instructions': this.assessment['force-instructions'],
            fullscreen: false
          }
        : { ...q, fullscreen: false };
      return { ...base, 'placeholder-values': this.placeholderValues };
    },
    getSlideVariant() {
      return (
        this.assessment?.['variant-slide'] ??
        this.theme?.value?.['variant-slide']
      );
    },
    scrollDown() {
      const el = this.$el;
      const sectionId = this.$el.closest('.section').id;
      const page = el?.closest('.page');
      page.unlockAndScroll(sectionId);
    },
    async playQuestionAudio(question) {
      // Tier 1: explicit prop-level audio on the question
      let questionAudio = question.audio ?? question.speech ?? null;

      if (!questionAudio) {
        if (this.audioAiJitEnabled) {
          // Tier 2: JiT synthesis via question's own schema (e.g. screen-question.schema.ts)
          questionAudio = await this.resolveChildIntro({
            ...question,
            'placeholder-values': this.placeholderValues
          });
        } else {
          // Tier 3: S3 pre-recorded file keyed by question id
          questionAudio = this.getAudioURL(`${question.id}`);
        }
      }

      this.currentAudio = question.id;
      if (questionAudio) play(questionAudio);
    },
    start() {
      stop();

      this.resetQuestions();
      this.activeSlide = 'assessment-introduction';
      this.animatedScore = 0;

      this.advanceSlide();
    },
    tryAgain() {
      this.resetIntroSlideStyle();
      this.start();
    },
    passCourse() {
      this.passed = true;
      set('passed', true);
      this.failed = false;
      set('failed', false);
      this.completeCourseAction();
    },
    failCourse() {
      this.passed = false;
      set('passed', false);
      this.failed = true;
      set('failed', true);
      this.failCourseAction();
    },
    exitCourse() {
      console.log(
        `Exit course - exceeded assessment max-attempts: ${this.maxAttempts}`
      );

      stop();
      setSessionTime();
      nextTick(() => {
        setExit();
        this.emit('exit');
      });
      this.hasSessionEnded = true;
    },
    resetIntroSlideStyle() {
      const introSlide = this.$el.querySelector(
        '.slides.questions .slide#assessment-introduction'
      );
      if (introSlide) {
        introSlide.style.opacity = '1';
      }
    },
    resetSummarySlideStyle() {
      const resultSlide = this.$el.querySelector(
        '.slides.questions .slide#assessment-result'
      );
      if (resultSlide) {
        resultSlide.style.opacity = '1';
      }
    },
    resetQuestions() {
      this.questions.forEach(q => {
        set(`questionOptions.${q.id}`, []);
        set(`correctQuestions.${q.id}`, 0);
        set(`completed.${q.id}`, 0);

        // if the question is a toggle, reset the options
        if (q.toggles) {
          q.toggles.forEach((t, i) => {
            set(`questionOptions.toggle_${q.id}_${i}_yes`, false);
            set(`questionOptions.toggle_${q.id}_${i}_no`, false);
            set(`completed.toggle_${q.id}_${i}`, false);
            set(`completed.toggle_${q.id}_${i}`, false);
          });
        }
      });

      this.includedQuestions = this.filterQuestions;
      this.submitted = [];
      this.syncPlaceholderValues();
    },
    getCorrectCount() {
      return this.includedQuestions.filter(this.isCorrect).length;
    },
    isCorrect(q) {
      return this.suspend.correctQuestions[q.id];
    },
    isSubmitted(q) {
      return this.suspend.completed[q.id];
    },
    hasTranslation(key, fallback) {
      // get the translation if it exists
      let maybeTranslated =
        this.translations?.[this.suspend.locale]?.[key] ||
        this.translations?.[this.suspend.locale]?.[fallback];

      return maybeTranslated || null;
    },
    async advanceSlide() {
      stop();

      this.openFeedback = null;

      this.resetIntroSlideStyle();
      this.resetSummarySlideStyle();

      const currentQuestionSlide = this.$el.querySelector(
        '.slides.questions .slide.slide--active'
      );
      const currentQuestionHeadingSlide = this.$el.querySelector(
        '.slides.question-headings .slide.slide--active'
      );

      // wait for the nav elements to render
      await nextTick();

      const assessmentNav = this.$el.querySelector('.assessment-nav');
      const progressBar = this.$el.querySelector('.progress-bar');
      const paginationDots = assessmentNav?.querySelectorAll(
        '.pagination .dot-container'
      );

      if (this.activeSlide === 'assessment-introduction') {
        // if the user has already attempted the assessment we skip to the first question
        if (this.attemptCount > 0) {
          this.activeSlide = 0;

          const activeQuestion = this.includedQuestions[this.activeSlide];

          if (activeQuestion) this.playQuestionAudio(activeQuestion);

          return;
        }

        if (progressBar) {
          gsap.set(progressBar, {
            opacity: 0,
            width: '1%'
          });
        }

        if (
          paginationDots &&
          paginationDots?.length > 0 &&
          this.showPaginationIcons
        ) {
          gsap.set(paginationDots, {
            opacity: 0,
            scale: '1%'
          });
        }

        const tl = gsap.timeline({ paused: true });

        if (currentQuestionHeadingSlide) {
          tl.to(
            currentQuestionHeadingSlide,
            {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out'
            },
            0
          );
        }

        if (currentQuestionSlide) {
          tl.to(
            currentQuestionSlide,
            {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out'
            },
            0
          );
        }

        // Animate progress bar growing from 10% to full width
        if (progressBar) {
          tl.to(
            progressBar,
            {
              opacity: 1,
              width: this.progressBarContainerWidth,
              duration: 0.6,
              ease: 'power2.out'
            },
            0.1
          );
        }

        // Fade in pagination dots after progress bar animation
        if (
          paginationDots &&
          paginationDots?.length > 0 &&
          this.showPaginationIcons
        ) {
          tl.to(paginationDots, {
            opacity: 1,
            scale: '100%',
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out'
          });
        }

        assessmentNav?.classList.remove('hidden');

        if (this.suspend.reducedMotion) {
          await tl.progress(1);
        } else {
          await tl.play();
        }

        this.activeSlide = 0;

        const activeQuestion = this.includedQuestions[this.activeSlide];

        if (activeQuestion) this.playQuestionAudio(activeQuestion);
      } else if (+this.activeSlide < this.includedQuestionCount - 1) {
        if (!(+this.activeSlide >= 0)) {
          return;
        }

        await this.scrollToAssessmentTop();

        // advance to next question
        this.activeSlide++;

        const activeQuestion = this.includedQuestions[this.activeSlide];

        if (activeQuestion) this.playQuestionAudio(activeQuestion);
      } else {
        // a timeline for coordinated animations
        const tl = gsap.timeline({ paused: true });

        if (currentQuestionHeadingSlide) {
          tl.to(
            currentQuestionHeadingSlide,
            {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out'
            },
            0
          );
        }

        if (currentQuestionSlide) {
          tl.to(
            currentQuestionSlide,
            {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out'
            },
            0
          );
        }

        if (paginationDots && paginationDots?.length > 0) {
          tl.to(
            paginationDots,
            {
              scale: 1,
              opacity: 0,
              duration: 0.3,
              ease: 'power2.out'
            },
            0
          );
        }

        if (progressBar) {
          tl.to(
            progressBar,
            {
              opacity: 0,
              width: 0,
              duration: 0.5,
              ease: 'power2.out'
            },
            0.2
          );
        }

        await this.scrollToAssessmentTop();

        if (this.suspend.reducedMotion) {
          await tl.progress(1);
        } else {
          await tl.play();
        }

        this.attemptCount++;
        this.activeSlide++;
        this.correctCount = this.getCorrectCount();

        set('assessment.correct', this.correctCount);
        set('assessment.total', this.totalCount);
        set('assessment.attemptCount', this.attemptCount);
        if (this.maxAttempts) {
          set('assessment.maxAttempts', this.maxAttempts);
        }
        set('assessment.completed', true);

        // Check if user has passed or failed
        if (this.score >= this.passmark) {
          this.passCourse();
        } else if (this.hasExceededMaxAttempts) {
          this.failCourse();
        }

        await this.$nextTick();

        this.animateScoreMeter();

        const resultKey = this.passed
          ? 'passed'
          : this.hasExceededMaxAttempts
            ? 'max-attempts'
            : 'failed';

        this.currentAudio = `${this.id}-results-${resultKey}`;

        if (this.audioAiJitEnabled) {
          this.syncPlaceholderValues();
          const overrideText = this.passed
            ? this.resultsPassed.text
            : this.hasExceededMaxAttempts
              ? this.resultsMaxAttemptsText
              : this.resultsFailedText;
          const audio = await this.resolveSlot(
            `results-${resultKey}`,
            overrideText
          );
          if (audio) play(audio);
        } else {
          play(this.getAudioURL(this.currentAudio));
        }
      }
    },
    async scrollToAssessmentTop() {
      // scroll back up for the next question
      const scroller = this.$el.closest('.scroller');
      const section = this.$el.closest('.section');
      if (scroller?.scrollTop > section?.offsetTop + 50) {
        if (this.suspend.reducedMotion) {
          scroller.scrollTo({
            top: section.offsetTop + 50,
            behavior: 'instant'
          });
        } else {
          await gsap.to(scroller, {
            scrollTo: { y: this.$el, offsetY: 50 },
            duration: 0.6,
            ease: 'power2.out'
          });
        }
      }
    },
    animateScoreMeter() {
      if (this.suspend.reducedMotion) {
        this.animatedScore = this.score;
        return;
      }

      this.animatedScore = 0;

      const scoremeter = this.$el.querySelector(
        '.assessment-result-scoremeter'
      );

      // animate the scoremeter fading in and scaling up from 10% to 100% before animating the score
      gsap.fromTo(
        scoremeter,
        {
          opacity: 0,
          scale: 0.1
        },
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          ease: 'power2.out'
        }
      );

      // animate the score from previous value to new value
      gsap.to(this, {
        delay: 0.5,
        duration: 2,
        animatedScore: this.score,
        ease: 'circ.out'
      });
    },
    getDeviceConstraints() {
      const iconSize = this.assessment['progress-bar']?.['icon-size']
        ? parseInt(this.assessment['progress-bar']?.['icon-size'])
        : 24;

      // Define device-specific constraints for progress bar layout
      if (this.largeOrLarger) {
        return {
          maxWidth: 1000,
          minWidth: 500,
          iconSize: iconSize,
          maxQuestions: 20
        };
      } else if (this.mediumOrLarger) {
        return {
          maxWidth: 700,
          minWidth: 500,
          iconSize: iconSize,
          maxQuestions: 15
        };
      } else if (this.smallOrLarger) {
        return {
          maxWidth: 600,
          minWidth: 400,
          iconSize: iconSize,
          maxQuestions: 10
        };
      } else {
        // here we hide pagination and show text
        return {
          maxWidth: 280,
          minWidth: 250,
          iconSize: iconSize,
          maxQuestions: 5
        };
      }
    },
    calculateOptimalWidth(questionCount, deviceConstraints) {
      const customWidth = this.assessment['progress-bar']?.width;
      const hideIcons =
        this.assessment['progress-bar']?.['hide-icons'] ?? false;

      // use non pixel-based custom width if we don't show icons (%, vw, etc.)
      if (customWidth && !customWidth.includes('px') && hideIcons) {
        return customWidth;
      }

      // Calculate spacing (this will be used by the grid layout)
      const { iconSize, maxWidth, minWidth, maxQuestions } = deviceConstraints;
      const spacing = this.calculateSpacing(questionCount, deviceConstraints);
      const spacingNum = parseInt(spacing);
      const totalIconWidth = questionCount * iconSize;
      const totalSpacingWidth = (questionCount - 1) * spacingNum;
      const requiredWidth = totalIconWidth + totalSpacingWidth;

      // Determine the optimal progress bar width based on the device size and the number of questions (icons) and the defined constraints
      let optimalWidth;

      if (questionCount <= maxQuestions) {
        optimalWidth = Math.min(maxWidth, Math.max(minWidth, requiredWidth));
      } else {
        optimalWidth = minWidth;
      }

      // use custom pixel width but keep it within constraints
      if (customWidth && customWidth.endsWith('px')) {
        if (hideIcons) {
          return (
            Math.min(
              parseInt(window.innerWidth),
              Math.max(minWidth, parseInt(customWidth))
            ) + 'px'
          );
        } else {
          return parseInt(customWidth) >= optimalWidth &&
            parseInt(customWidth) < parseInt(window.innerWidth)
            ? customWidth
            : `${maxWidth}px`;
        }
      }

      return `${optimalWidth}px`;
    },
    calculateSpacing(questionCount, deviceConstraints) {
      const { iconSize } = deviceConstraints;
      let spacing;

      // The minimum spacing between icons needs to be at least the specified width of the icon to avoid overlap. We use some weighting depending on the device size
      if (this.largeOrLarger) {
        spacing = iconSize * 2;
      } else if (this.mediumOrLarger) {
        spacing = iconSize * 1.5;
      } else {
        spacing = iconSize * 1;
      }

      return `${spacing}px`;
    }
  }
};
</script>

<style scoped>
.slide {
  transition: transform 0.4s ease-out;
  transform: translateZ(0);
}

.reduce-motion .slide {
  transition: none;
}

@keyframes spin {
  0% {
    transform: rotateZ(0);
  }

  100% {
    transform: rotateZ(720deg);
  }
}

@keyframes flip {
  0% {
    transform: rotateY(0);
  }

  100% {
    transform: rotateY(360deg);
  }
}

@keyframes jiggle {
  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}

/* .assessment {
  padding-left: var(--edge-padding);
} */

.align-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.assessment-nav {
  /* center the progress bar and pagination */
  & .progress-bar,
  & .pagination {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  & .progress-bar {
    & .fill {
      width: 0%;
      height: 100%;
      transition-delay: 0.5s;
      transition: width 0.4s ease-out;
      transform: translateZ(0);
    }

    .reduce-motion & .fill {
      transition: none;
    }
  }

  & .pagination {
    width: 100%;
    height: 100%;

    & .dot-container {
      /*transition:
        scale 0.6s ease-out,
        transform 0.6s ease-out;
      transform: scale(100%);
      transform-origin: center center;

      &.-active {
        transition-delay: 0.5s;
        transform: scale(130%);
      }*/

      & .dot-base {
        &.correct {
          animation: flip 1s ease-out both;
        }

        &.incorrect {
          animation: jiggle 0.4s ease-out both;
          animation-iteration-count: 2;
        }
      }

      .reduce-motion & .dot-base {
        &.correct,
        &.incorrect {
          animation: none;
        }
      }
    }
  }
}

.assessment-result-scoremeter {
  --score-percent: 0;
  --passmark: 0;
  --main-size: 220px;
  --ring-width: 10px;
  --score-text-size: 70px;
  --ring-score-color: rgb(var(--color-primary-500));
  --ring-base-color: rgb(var(--color-primary-200));
  --ring-base-color-dark: rgb(var(--color-primary-900));
  --ring-tick-color: rgb(var(--color-primary-400) / 0.5);
  --score-text-color: rgb(var(--color-primary-900));
  --score-text-color-dark: rgb(var(--color-primary-200));

  position: relative;
  width: var(--main-size);
  height: var(--main-size);
  margin: 0 auto;
  transform-origin: center center;

  .score-ticks {
    .tick {
      position: absolute;
      width: 2px;
      height: var(--main-size);
      background: var(--ring-tick-color);
      left: 50%;
      top: 0;
      z-index: 1;
      transform: rotate(calc(360deg / 100 * var(--tick-index)));
      clip-path: inset(0 0 calc(100% - calc(var(--ring-width) - 1px)) 0);
    }
  }

  .score-dial {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: var(--ring-width) solid transparent;
  }

  &:not(.dark) .score-dial {
    background:
      linear-gradient(white, white) padding-box,
      conic-gradient(
          var(--ring-score-color) calc(var(--score-percent) * 1%),
          var(--ring-base-color) calc(var(--score-percent) * 1%)
        )
        border-box;
  }

  &.dark .score-dial {
    background:
      linear-gradient(var(--ring-base-color-dark), var(--ring-base-color-dark))
        padding-box,
      conic-gradient(
          var(--ring-score-color) calc(var(--score-percent) * 1%),
          var(--ring-base-color) calc(var(--score-percent) * 1%)
        )
        border-box;
  }

  .score-text {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--score-text-size);
    font-weight: bold;
    color: var(--score-text-color);
  }

  &.dark .score-text {
    color: var(--score-text-color-dark);
  }
}
</style>

<style>
.screen.screen--assessment .assessment .slide .screen-next-button {
  display: none;
}
</style>
